import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import {
  FileUpload,
  FileUploadHeaderTemplateOptions,
  FileUploadOptionsType,
} from 'primereact/fileupload'
import { TabPanel, TabView } from 'primereact/tabview'
import { Tag } from 'primereact/tag'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'
import React, { Fragment, useRef, useState } from 'react'
import { AddDossierForm } from './Dialogs/AddDossierForm'
import { EducationReportProposalDialog } from './Dialogs/EducationReportProposalDialogs'
import { TrialReportProposalDialog } from './Dialogs/TrialReportProposalDialog'
import { claims } from '../../../claims'
import { API_ANALYZE, MAX_FILE_SIZE } from '../../../constants'
import { RequestMethod } from '../../../model/enums/enums'
import { ocrDocumentProposal } from '../../../model/enums/ocrEnums'
import { ocrProposal, OCRResponse } from '../../../model/ocrProposal.model'
import { useAppSelector } from '../../../redux/hooks'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { findApprenticeById } from '../../../util/arraySearch'
import { getActiveSemester } from '../../../util/getActiveSemester'
import { hasAnyRequiredClaim } from '../../../util/validation/hasClaim'
import { cancelOptions, uploadOptions } from '../../util/form/MokaiUpload'

const chooseFileOptions: FileUploadOptionsType = {
  icon: 'fas fa-folder-open fa-lg p-2 pl-3 ml-2',
  label: ' ',
  style: { width: '30px', height: '30px', textAlign: 'center' },
  className:
    'float p-button-rounded p-button-text justify-content-center align-items-center p-2 m-0 mr-2',
}
export const OcrDrop = (): JSX.Element => {
  const [validDropArea, setValidDropArea] = useState<boolean>(false)

  const userClaims = useAppSelector((state) => state.claims)

  const [proposalAnswerState, setProposalAnswer] = useState<
    ocrProposal | undefined
  >(undefined)
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)

  // this is mostly used for text-displaying
  const [ocrResult, setOcrResult] = useState<ocrDocumentProposal>(
    ocrDocumentProposal.None
  )
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [OcrUploadMode, setOcrUploadMode] = useState<boolean>(true)

  // used for tab switching
  // default is dossier
  const [activeIndex, setActiveIndex] = useState<number>(
    ocrDocumentProposal.Unknown
  )

  const apprentices = useAppSelector((state) => state.apprentices)

  let uploadToOCR = true

  const toast = useRef<Toast>(null)
  const fileUploadRef = useRef<FileUpload>(null)

  const getActiveSemesterByStartAndEnd = (
    apprenticeStart: any,
    apprenticeEnd: any
  ): number => {
    let activeSemester: number
    if (apprenticeStart && apprenticeEnd) {
      const apprenticeStartDate = new Date(apprenticeStart)
      const apprenticeEndDate = new Date(apprenticeEnd)
      const today = new Date()
      const totalApprenticeshipDurationInYears =
        apprenticeEndDate.getFullYear() - apprenticeStartDate.getFullYear()
      activeSemester = getActiveSemester(
        apprenticeStartDate,
        totalApprenticeshipDurationInYears
      )
      if (today < apprenticeStartDate) {
        activeSemester = 1
      }
      if (today > apprenticeEndDate) {
        activeSemester = totalApprenticeshipDurationInYears * 2
      }
    } else {
      activeSemester = 0
    }
    return activeSemester
  }

  const onBeforeDrop = (event: DragEvent) => {
    let filesList = event.dataTransfer?.files
    if (filesList) {
      const files = Array.from(filesList)
      if (files.filter((e) => e.size > MAX_FILE_SIZE).length > 0) {
        toast?.current?.show({
          severity: 'error',
          summary: t<string>('ocr.upload_failed'),
          detail: t<string>('ocr.exceeded_max_size', {
            maxSize: `${MAX_FILE_SIZE / 10e6}mb`,
          }),
        })
        fileUploadRef.current?.clear()
        return false
      } else if (files.filter((e) => e.type !== 'application/pdf').length > 0) {
        uploadToOCR = false
        return true
      } else if (files.length > 1) {
        uploadToOCR = false
        return true
      }
      uploadToOCR = true
      return true
    }
    return false
  }

  const uploadFile = async (event: any) => {
    setValidDropArea(false)

    toast?.current?.show({
      severity: 'success',
      summary: t<string>('ocr.uploaded'),
      detail: t<string>('ocr.analyzing'),
    })

    if (event.files && event.files.length > 0) {
      const firstFile = event.files[0]

      // in case the button and not the DnD field is used, we have to check again
      const fileType = firstFile.type.toLowerCase()
      if (fileType !== 'pdf' && fileType !== 'application/pdf') {
        uploadToOCR = false
        console.info(
          '[File Drop] File is not in PDF-Format, file is not analyzed.'
        )
      }
      if (event.files?.length > 1) {
        uploadToOCR = false
        console.info(
          '[File Drop] More than one file is added, files are not analyzed.'
        )
      }

      if (uploadToOCR && firstFile) {
        const res = await handleRequest<OCRResponse>(
          API_ANALYZE(),
          RequestMethod.UPLOAD_MEDIA,
          firstFile,
          'signedFile'
        ).catch(() => {
          console.log('Failed to analyze file')
        })
        let data_arr = res?.data?.data
        let data: any = undefined
        if (res?.data?.type === 'EducationReport') {
          if (data_arr && data_arr.length > 0) {
            data = data_arr[0]
          }
          const semester = getActiveSemesterByStartAndEnd(
            data?.contract_start,
            data?.contract_end
          )
          const id_apprentice = data?.apprentice_id ? data.apprentice_id : 0
          const id_responsible = data?.supervisor_id ? data.supervisor_id : 0
          const proposal: ocrProposal = {
            id_apprentice: id_apprentice,
            id_responsible: id_responsible,
            semester_report: semester,
          }

          setProposalAnswer(proposal)
          setOcrResult(ocrDocumentProposal.EducationReport)
          setActiveIndex(ocrDocumentProposal.EducationReport)
        } else if (res?.data?.type === 'TrialPeriodReport') {
          if (data_arr && data_arr.length > 0) {
            data = data_arr[0]
          }
          const id_apprentice = data?.apprentice_id ? data.apprentice_id : 0
          const id_responsible = data?.supervisor_id ? data.supervisor_id : 0
          const proposal: ocrProposal = {
            id_apprentice: id_apprentice,
            id_responsible: id_responsible,
            semester_report: undefined,
          }
          setOcrResult(ocrDocumentProposal.TrialPeriodReport)
          setProposalAnswer(proposal)
          setActiveIndex(ocrDocumentProposal.TrialPeriodReport)
        } else {
          setOcrResult(ocrDocumentProposal.Unknown)
        }
      } else {
        setOcrResult(ocrDocumentProposal.Unknown)
      }
      setDialogVisible(true)
      setUploadedFiles(event.files)
    }
  }

  const onTemplateRemove = (file: any, callback: any) => {
    //setTotalSize(totalSize - file.size)
    callback()
  }

  const itemTemplate = (file: any, properties: any): JSX.Element => {
    let documentName = file?.name
    let icon = <i className={'fas fa-file-pdf icon-blue'} />
    if (documentName && documentName.length > 20) {
      documentName = `${documentName.substring(0, 17)}...`
    }
    return (
      <Fragment>
        <div className="flex justify-content-center align-items-center">
          {icon}
          <span className="text-left p-0 ml-2 small-text">{documentName}</span>
        </div>
        <Tag value={properties.formatSize} className="mr-1" />
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-danger p-button-text"
          onClick={() => onTemplateRemove(file, properties.onRemove)}
        />
      </Fragment>
    )
  }

  const emptyTemplate = (): JSX.Element => {
    return (
      <div
        onDragEnter={() => {
          setValidDropArea(true)
        }}
        onDragLeave={() => {
          setValidDropArea(false)
        }}
        className={`flex align-items-center pl-1 pr-1 pt-1 mt-1 pb-1 mb-1 button-height w-full
                    ${validDropArea ? ' drag-hover ' : ''}`}
        style={{
          background: 'transparent',
          opacity: '0.5',
        }}
      >
        <span className="fa-solid fa-cloud-arrow-up icon-blue mr-2 fa-xl min-width-220 center-icon" />
      </div>
    )
  }

  const resetState = () => {
    setDialogVisible(false)
    setProposalAnswer(undefined)
    setOcrResult(ocrDocumentProposal.None)
    setActiveIndex(ocrDocumentProposal.Unknown)
    setUploadedFiles([])
    uploadToOCR = true
    setOcrUploadMode(true)
    fileUploadRef.current?.clear()
  }

  const selectFileButton = (options: FileUploadHeaderTemplateOptions) => {
    const { chooseButton } = options
    return <div className={'flex p-button-text'}>{chooseButton}</div>
  }

  const getPersonIDByApprentice = () => {
    if (!proposalAnswerState?.id_apprentice) return undefined

    return findApprenticeById(proposalAnswerState.id_apprentice, apprentices)
      ?.person_id
  }

  const deleteSingleFile = (idx: number) => {
    const files = [...uploadedFiles]
    files.splice(idx, 1)
    setUploadedFiles(files)
  }

  const getCurrentDocType = (): string => {
    if (ocrResult === ocrDocumentProposal.TrialPeriodReport) {
      return 'Probezeitbericht'
    } else if (ocrResult === ocrDocumentProposal.EducationReport) {
      return 'Bildungsbericht'
    }
    // should never happen
    return ''
  }

  return (
    <>
      {hasAnyRequiredClaim(userClaims, [
        claims.ocrApi.main,
        claims.ocrApi.analyze,
      ]) && (
        <>
          <Toast ref={toast} />
          <Dialog
            visible={dialogVisible && ocrResult !== ocrDocumentProposal.None}
            onHide={resetState}
            header={
              ocrResult === ocrDocumentProposal.Unknown
                ? t<string>('ocr.document_not_found')
                : ocrResult === ocrDocumentProposal.EducationReport
                ? t<string>('ocr.education_report_found')
                : t<string>('ocr.trial_report_found')
            }
            style={{ width: 'auto', height: 'auto' }}
          >
            {ocrResult === ocrDocumentProposal.Unknown ? (
              <>
                <p>
                  <span
                    className={
                      'fa-solid fa-exclamation-triangle mr-2 icon-orange'
                    }
                  />
                  <span className={'help-text'}>
                    {!OcrUploadMode
                      ? t<string>('ocr.non_ocr')
                      : t<string>('ocr.unknown_document_help')}
                  </span>
                </p>
              </>
            ) : (
              <>
                <p className={'mb-2 secondary-text'}>
                  {t<string>('ocr.review_selection')}
                </p>
                <span className={'fa-solid fa-file icon-blue mr-2'} />
                <b>{uploadedFiles?.at?.(0)?.name}</b>
                {activeIndex === ocrDocumentProposal.Unknown && (
                  <p>
                    <span
                      className={
                        'fa-solid fa-exclamation-triangle mr-2 icon-orange'
                      }
                    />
                    {t<string>('ocr.dossier_warning', {
                      typ: getCurrentDocType(),
                    })}
                  </p>
                )}
              </>
            )}
            {/* ocrDocumentPropose.None should never happen with the dialog open */}
            {/*0: education report, 1: trial period report, 2: dossier */}
            <TabView
              className={'mt-2'}
              renderActiveOnly={true}
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
            >
              <TabPanel
                disabled={!OcrUploadMode}
                header={t<string>('title.education_report')}
              >
                <EducationReportProposalDialog
                  defaultValues={proposalAnswerState}
                  file={uploadedFiles[0]}
                  toastRef={toast}
                  closeCallback={resetState}
                />
              </TabPanel>
              <TabPanel
                disabled={!OcrUploadMode}
                header={t<string>('title.trial_period_report')}
              >
                <TrialReportProposalDialog
                  defaultValues={proposalAnswerState}
                  file={uploadedFiles[0]}
                  toastRef={toast}
                  closeCallback={resetState}
                />
              </TabPanel>
              <TabPanel header={t<string>('title.dossier')}>
                <AddDossierForm
                  deleteCallback={deleteSingleFile}
                  defaultNumber={getPersonIDByApprentice()}
                  files={uploadedFiles}
                  toastRef={toast}
                  closeCallback={resetState}
                />
              </TabPanel>
            </TabView>
          </Dialog>

          <FileUpload
            ref={fileUploadRef}
            auto={true}
            className={'transparent-bg drag-box ocr-file-drag'}
            customUpload={true}
            headerTemplate={selectFileButton}
            chooseOptions={chooseFileOptions}
            uploadOptions={uploadOptions}
            cancelOptions={cancelOptions}
            emptyTemplate={emptyTemplate}
            itemTemplate={itemTemplate}
            uploadHandler={(e) => uploadFile(e)}
            onBeforeDrop={(e) => onBeforeDrop(e)}
            contentStyle={{
              backgroundColor: validDropArea ? '#ADCBBF' : 'transparent',
              transition: '1s ease',
              border: '1px dashed  rgb(197, 198, 200)',
              borderRadius: '6px',
            }}
            multiple={true}
          ></FileUpload>
          <Tooltip
            target={'.ocr-file-drag'}
            position={'bottom'}
            appendTo={document.body}
            autoHide={true}
            style={{ width: '340px' }}
            showDelay={1200}
            className={'legend-tooltip'}
          >
            <div className={'grid p-2 small-text'}>
              <p className={'col-12'}>
                <span className={'fa-solid fa-folder-open icon-blue'} />
                <span className={'ml-1'}>
                  {t<string>('ocr.explanation.folder')}
                </span>
              </p>
              <p className={'col-12'}>
                <span className={'fa-solid fa-cloud-upload icon-light-blue'} />
                <span className={'ml-1'}>
                  {t<string>('ocr.explanation.drop')}
                </span>
              </p>
              <p className={'col-12'}>
                <span className={'fa-solid fa-info-circle icon-dark-gray'} />
                <span className={'ml-1'}>
                  {t<string>('ocr.explanation.types')}
                </span>
              </p>
              <p className={'col-12'}>
                <span className={'fa-solid fa-info-circle icon-dark-gray'} />
                <span className={'ml-1'}>
                  {t<string>('ocr.explanation.control')}
                </span>
              </p>
            </div>
          </Tooltip>
        </>
      )}
    </>
  )
}